<template>
    <div class="wrap">
        <div :style="virtualScrollMargins">
            <div class="cv-row font-6"
                 v-for="(value, index) in viewedSteps" :key="value.key">
                <div class="cv-col col-cv-key">
                    {{ value.key }}
                </div>
                <div class="cv-col col-range">
                    <span>
                        {{ isCVZero(value.key) ? '' : '+ ' }}{{ value.min }}{{centSymbol}}
                    </span>
                    <IconBase :height="7"
                              :width="14"
                              verticalAlign="middle"
                              iconName="arrowSortRightIcon"
                              :iconColor="consts.COLORS.LIGHT_600"
                              class="arrow-icon"
                              v-if="showArrow(value)"
                    />
                    <form novalidate class="cv-value-input-wrap" :class="{'read-mode': !readonly && showCentSymbol(value)}">
                        <span v-if="isCVInfinity(value.max)" class="infinity">
                            &infin;
                        </span>
                        <span v-else-if="readonly">
                            {{ value.max === 0 ? null : value.max }}{{showCentSymbol(value) ? centSymbol : ''}}
                        </span>
                        <input type="number"
                               class="cv-value-input"
                               :value="value.max"
                               :min="inputMin(value)"
                               :max="inputMax(value)"
                               placeholder="Value"
                               :step="inputStep()"
                               @focus="value.focused = true"
                               @blur="onBlur(value)"
                               @mouseenter="value.hovered = true"
                               @mouseleave="value.hovered = false"
                               @keyup.enter="handleEnterKepUp($event, value)"
                               @keydown.enter="handleEnterKeyDown($event)"
                               v-else
                               @change="handleCvChange(index, $event)"
                               @input="handleCVEdit(index, $event)"
                               :ref="`valueInputs${value.key}`"
                               :class="{
                                   'cv-value-input-error':
                                        validatorFullSteps[value.key] && validatorFullSteps[value.key].$anyError && value.max !== INPUT_INITIAL_VALUE
                               }"
                               :disabled="$v.$anyError
                                          && validatorFullSteps[value.key] && !validatorFullSteps[value.key].$invalid"
                        >
                    </form>
                    <span v-if="validatorFullSteps[value.key]
                                && validatorFullSteps[value.key].$anyError && value.max !== INPUT_INITIAL_VALUE">
                        <Tooltip :text="getValidationError(validatorFullSteps[value.key])">
                            <IconBase iconName="notificationErrorIcon"
                                      :width="12"
                                      :height="12"
                                      verticalAlign="middle"
                                      :iconColor="consts.COLORS.NEGATIVE_500"
                                      class="validation-icon"
                            />
                        </Tooltip>
                    </span>
                </div>
                <div class="cv-col">
                    <div class="stats-bar" :class="{'stats-bar-readonly': readonly}">
                        <PercentBar :font="6"
                                    :emptySpaceColor="consts.COLORS.LIGHT_200"
                                    :filledSpaceColor="consts.COLORS.PRIMARY_200"
                                    :maxVal="maxPercent"
                                    :val="getSOV(value)"
                                    :actualValue="getUsersCount(value)"
                                    :showActualValue="true"
                                    :loading="statsLoading"
                                    :borderRadius="4"
                        ></PercentBar>
                    </div>
                    <span class="actions"
                          v-if="showActions(value)">
                        <button @click="deleteStep(value, index)"
                                :disabled="isDeleteDisabled(value)"
                        >
                            <IconBase iconName="deleteIcon"
                                      :width="10"
                                      :height="11"
                                      verticalAlign="middle"
                                      display="block"
                                      class="button-icon"
                                      :iconColor="getDeleteIconColor(validatorFullSteps[value.key])"/>
                        </button>
                        <button @click="addStep(value, index)"
                                :disabled="isAddDisabled(value)"
                        >
                            <IconBase iconName="plusBoldIcon"
                                      :width="10"
                                      :height="9"
                                      verticalAlign="middle"
                                      display="block"
                                      class="button-icon"
                                      :iconColor="getAddIconColor(value)"/>
                        </button>
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import IconBase from 'components/IconBase.vue';
import consts from 'services/metaData/consts';
import Tooltip from 'components/Tooltip/Tooltip.vue';
import MessagesHelper from 'services/validators/messagesHelper';
import PercentBar from 'components/percentBar/PercentBar.vue';
import {
    formatRangesForGet,
    statsApiPath,
    MIN_STEP_JUMP,
    CENT_SYMBOL,
    roundValueToNearestStep
} from 'pages/conversionValue/cvManager/serviceUtils';
import { mapActions, mapMutations } from 'vuex';
import validators from 'services/libWrappers/vuelidate/validators';
import http from 'services/utils/http';
import virtualScroll from '@/mixins/virtualScroll';
import { adRevenueMax, maxSteps, adRevenueMin } from './consts';


const { helpers } = validators;
const itemHeight = 48;
const INPUT_EMPTY_VALUE = '';
const INPUT_INITIAL_VALUE = null;

export default {
    name: 'CVManagerCVMap',
    components: { PercentBar, Tooltip, IconBase },
    mixins: [
        virtualScroll(itemHeight)
    ],
    data() {
        return {
            consts,
            maxSteps,
            adRevenueMax,
            adRevenueMin,
            stepsLocal: null,
            maxUsers: 0,
            stats: {},
            statsLoading: false,
            maxPercent: 0,
            INPUT_EMPTY_VALUE,
            INPUT_INITIAL_VALUE
        };
    },
    props: {
        steps: Array,
        apps: Array,
        readonly: Boolean,
        cvLoaded: Boolean
    },
    validations: {
        fullSteps: {
            $each: {
                max: {
                    between: helpers.withParams(
                        { max: adRevenueMax, min: adRevenueMin },
                        (value) => (value >= adRevenueMin && value < (adRevenueMax + 0.01))
                            || value === Infinity || value === null
                    )
                },
                minMax: (step) => {
                    const max = roundValueToNearestStep(step.max);
                    const { min, key } = step;
                    return (key === 0 && max >= min) || (key > 0 && min < max);
                },
                required: (step) => step.max !== INPUT_EMPTY_VALUE
            }
        }
    },
    watch: {
        steps: {
            immediate: true,
            handler() {
                this.stepsLocal = this.steps.map((step) => ({
                    ...step,
                    focused: step?.focused || false,
                    hovered: step?.hovered || false
                }));
                this.getStatsData(this.steps);
                this.onScroll();
            }
        },
        apps() {
            this.getStatsData(this.steps);
        },
        cvLoaded() {
            this.getStatsData(this.steps);
        }
    },
    created() {
        const calcMax = (step) => {
            if (step.max === INPUT_INITIAL_VALUE || step.max === INPUT_EMPTY_VALUE) {
                return INPUT_EMPTY_VALUE;
            }
            return +step.max;
        };
        this.$v.$touch();
        this.$on('validateSteps', () => {
            this.$set(this, 'stepsLocal', this.stepsLocal.map((step) => ({
                ...step,
                max: calcMax(step)
            })));
            this.$v.$touch();
            const firstStepWithError = Object.values(this.validatorFullSteps)
                .find((step) => step.$anyError);
            if (firstStepWithError) {
                this.scrollerRef.scrollTop = firstStepWithError.$model.key * itemHeight;
            }
        });
    },
    computed: {
        centSymbol() {
            return CENT_SYMBOL;
        },
        minStepJump() {
            return MIN_STEP_JUMP;
        },
        viewedSteps() {
            if (this.virtualScroll) {
                return this.fullSteps.slice(this.viewedFirstIndex, this.viewedLastIndex);
            }
            return this.fullSteps;
        },
        itemsLength() {
            return this.fullSteps.length;
        },
        fullSteps() {
            if (!this.steps || !this.stepsLocal) return [];
            const steps = [...this.stepsLocal];
            this.addInfinityStepIfLastStepIsValid(steps);
            return steps;
        },
        validatorFullSteps() {
            return this.$v.fullSteps.$each.$iter;
        }
    },
    methods: {
        ...mapMutations({
            clearCache: 'cvStats/clearCache'
        }),
        ...mapActions({
            getWithCache: 'cvStats/getWithCache'
        }),
        isZeroShown({ key, max }) {
            return !(this.readonly && this.isCVZero(key) && max === 0);
        },
        inputMin(value) {
            const { min, key } = value;
            if (this.isCVZero(key)) {
                return min;
            }
            return min + this.minStepJump;
        },
        handleEnterKepUp(e, value) {
            e.preventDefault();
            this.$refs[`valueInputs${value.key}`][0].blur();
        },
        handleEnterKeyDown(e) {
            e.preventDefault();
        },
        inputStep() {
            return this.minStepJump;
        },
        inputMax(value) {
            const { max, key } = value;
            if (this.isCVZero(key) && !max && max !== 0) {
                return 0;
            }
            return adRevenueMax;
        },
        onBlur(value) {
            this.$v.$touch();
            const { max } = value;
            if (!max && max !== 0) {
                value.max = 0;
                this.handleCvChange(value.key);
            }
            value.focused = false;
        },
        addInfinityStepIfLastStepIsValid(steps) {
            const len = this.steps.length;
            const lastIndex = this.steps.length - 1;
            const localLastIndex = this.stepsLocal.length - 1;
            const lastStep = this.stepsLocal[localLastIndex];
            const beforeLast = this.stepsLocal[localLastIndex - 1];
            const lastStepValid = len > 1
                && lastStep.max !== null
                && lastStep.max > beforeLast.max
                && lastStep.max <= adRevenueMax
                && lastStep.max > 0;
            if (len >= 1 && this.steps[lastIndex].max !== null
                && (localLastIndex === lastIndex
                    || (lastStep.focused === false && lastStepValid))) {
                steps.push({
                    key: parseInt(lastStep.key, 10) + 1,
                    min: this.steps[lastIndex].max,
                    max: Infinity
                });
            }
        },
        async getStatsData(steps) {
            try {
                if (!this.cvLoaded) return;
                this.statsLoading = true;
                const formattedRanges = formatRangesForGet(steps);
                if (formattedRanges.length) {
                    if (this.perviousCallCancelToken) {
                        this.perviousCallCancelToken.cancel();
                    }
                    this.perviousCallCancelToken = http.createCancelToken();
                    this.stats = await this.getWithCache({
                        path: statsApiPath,
                        payload: {
                            applications: this.apps,
                            ranges: formattedRanges
                        },
                        cancelToken: this.perviousCallCancelToken
                    });
                }
                this.calculateSOV();
            } catch (error) {
                const isCancel = http.isCancel(error);
                if (isCancel) return;

                // show an empty bar
                this.stats = {};
            }
            this.statsLoading = false;
        },
        calculateSOV() {
            let maxPercent = 0;
            const usersSum = Object.keys(this.stats).reduce((acc, key) => acc + this.stats[key].usersCount, 0);
            Object.keys(this.stats).forEach((key) => {
                const val = this.stats[key].usersCount;
                this.stats[key].sov = (val / usersSum) * 100;

                if (this.stats[key].sov > maxPercent) {
                    maxPercent = this.stats[key].sov;
                }
            });
            this.maxPercent = maxPercent;
        },
        isValidValueModel(valueModel) {
            const max = valueModel?.max;
            return typeof max === 'number';
        },
        getSOV(valueModel) {
            if (!this.isValidValueModel(valueModel)) { return 0; }
            const key = this.getStatsKey(valueModel);
            const sov = this.stats?.[key]?.sov;
            if (!sov) {
                return 0;
            }
            return Number.parseFloat(sov.toLocaleString('en', { maximumFractionDigits: 2 }));
        },
        getUsersCount(valueModel) {
            if (!this.isValidValueModel(valueModel)) { return null; }
            const key = this.getStatsKey(valueModel);
            return this.stats?.[key]?.usersCount || null;
        },

        getStatsKey(valueModel) {
            const { min, max, key } = valueModel;
            if (this.isCVZero(key)) {
                return `0_${max}`;
            }
            if (this.isCVInfinity(max)) {
                return `${min + this.minStepJump}_-1`;
            }
            return `${min + this.minStepJump}_${max}`;
        },
        isCVZero(cvKey) {
            // eslint-disable-next-line eqeqeq
            return cvKey == 0;
        },
        isDeleteDisabled(value) {
            return (this.$v.$anyError && this.validatorFullSteps[value.key]
             && !this.validatorFullSteps[value.key].$invalid) || this.stepsLocal?.length === 1;
        },
        isCVInfinity(max) {
            return max === Infinity;
        },
        showActions(step) {
            return !this.readonly
                && !this.isCVInfinity(step.max);
        },
        showCentSymbol({
            key, focused, hovered, max
        }) {
            const isZeroShown = this.isZeroShown({ key, max });
            if (!isZeroShown || hovered || focused) {
                return false;
            }
            return !this.isCVInfinity(max);
        },
        showArrow(step) {
            const isViewMode = this.readonly;
            const isMaxExist = !!step.max;
            return (isMaxExist || !isViewMode);
        },
        handleCVEdit(index, event) {
            const { value } = event.target;
            this.$set(this.stepsLocal, index, { ...this.stepsLocal[index], max: value === INPUT_EMPTY_VALUE ? INPUT_EMPTY_VALUE : +value });
        },
        async handleCvChange(index) {
            let value = this.stepsLocal[index].max;
            value = roundValueToNearestStep(value);
            this.$set(this.stepsLocal, index, { ...this.stepsLocal[index], max: value });
            await this.$nextTick();
            if (!this.$v.$anyError) {
                const changedStep = { ...this.stepsLocal[index], max: value };
                this.$set(this.stepsLocal, index, changedStep);

                // if there are steps after the one edited, modify them accordingly
                if (index < (this.stepsLocal.length - 1)) {
                    // filter out steps that are included in the new step
                    this.stepsLocal = this.stepsLocal.filter(
                        (step) => changedStep.key === step.key
                            || !(step.min >= changedStep.min && step.max && step.max <= value)
                    );

                    // modify next step min if it is partially included in the new step,
                    // or if it should include the data removed from this step
                    const changedStepIndex = this.stepsLocal.indexOf(changedStep);
                    const nextStepIndex = changedStepIndex + 1;
                    if (changedStepIndex < (this.stepsLocal.length - 1)) {
                        const changedNextStep = { ...this.stepsLocal[nextStepIndex], min: value };
                        this.$set(this.stepsLocal, nextStepIndex, changedNextStep);
                    }
                }

                // re evaluate cv keys
                this.evaluateCvs();

                // set the new value
                this.$emit('update:steps', this.stepsLocal);
            }
        },
        evaluateCvs() {
            // re evaluate cv keys
            this.stepsLocal = this.stepsLocal.map((cv, cvIndex) => ({ ...cv, key: cvIndex }));
        },
        deleteStep(step, index) {
            const indexInt = parseInt(index, 10);
            if (this.stepsLocal[indexInt + 1]) {
                this.stepsLocal[indexInt + 1] = { ...this.stepsLocal[indexInt + 1], min: step.min };
            }
            this.stepsLocal.splice(indexInt, 1);
            this.evaluateCvs();
            this.$emit('update:steps', this.stepsLocal.slice());
        },
        getInputByIndex(index) {
            return this.$refs[`valueInputs${this.stepsLocal[index].key}`][0];
        },
        async addStep(step, index) {
            this.onBlur(step);
            const indexToInsert = parseInt(index, 10) + 1;
            const min = step.max;
            this.stepsLocal.splice(indexToInsert, 0, {
                min,
                max: null,
                focused: false,
                hovered: false
            });

            this.evaluateCvs();
            await this.$nextTick();
            const input = this.getInputByIndex(indexToInsert);
            input.focus();
        },
        getValidationError(validation) {
            if (!validation.required) {
                return 'Mandatory field';
            }
            if (!validation.minMax) {
                return 'Invalid ad revenue range';
            }
            // if between validation failed
            if (!validation.max.between) {
                return MessagesHelper.getErrorMessages(
                    'Ad revenue',
                    'max',
                    validation
                )[0];
            }
            return '';
        },
        getDeleteIconColor(value) {
            return (this.$v.$anyError && value && !value.$invalid) || this.stepsLocal?.length === 1 ? consts.COLORS.LIGHT_700 : consts.COLORS.DARK_500;
        },
        isAddDisabled(value) {
            const { max, key } = value;
            if (key === 0 && this.itemsLength === 1) {
                return false;
            }
            return (this.$v.$anyError || max === adRevenueMax || this.itemsLength >= this.maxSteps);
        },
        getAddIconColor(value) {
            if (this.isAddDisabled(value)) {
                return consts.COLORS.LIGHT_700;
            }
            return consts.COLORS.DARK_500;
        }
    }
};
</script>

<style scoped lang="scss">
@import "~styles/styleGuide";
@import "~styles/input";

.cv-row {
    display: grid;
    grid-template-columns: 80px 225px auto;
    color: $light-800;
    line-height: 15px;
    border-bottom: 1px solid $light-200;
    border-top: 1px solid $light-200;
    border-radius: 4px;
    background-color: $white;

    @media #{$desktopAndAbove} {
        grid-template-columns: 80px 408px auto;
    }

    .cv-col {
        padding-left: 32px;
        padding-top: 4px;
        padding-bottom: 4px;
        line-height: 40px;

        &.col-range {
            display: grid;
            grid-template-columns: 60px 15px 100px auto;
            position: relative;

            // voodoo, padding affects some of the rows grid weirdly
            padding-left: 0;
            margin-left: 32px;
        }
    }
}
.col-cv-key {
    color: $dark-300;
}

.cv-value-input-wrap {
    padding-left: 24px;
    height: 40px;

    &.read-mode:after {
        content: '\A2';
        color: $light-900;
        @include font(6);
        position: absolute;
        left: 157px;
        top: 4px;
    }
}

input[type='number'].cv-value-input {
    height: 32px;
    background-color: $light-300;
    color: $dark-300;
    border: none;
    width: 72px;
    padding-right: 0;

    &:focus {
        border-bottom: 2px solid $primary-500;
        background-color: $light-300;
        &:after {
            content: '';
        }
    }
    &:hover:after {
        content: '';
    }
    &.cv-value-input-error {
        border-bottom: 2px solid $negative-500;
    }
    &:disabled {
        color: $light-800;
        background-color: $light-200;
    }
    &::placeholder, &::-webkit-input-placeholder, &:-ms-input-placeholder {
        color: $light-800;
        @include font(7);
    }
}

.validation-icon {
    margin-left: 5px;
}

.actions {
    float: right;
    padding-right: 8px;
    button, button:focus, button:active {
        background-color: $light-300;
        border-radius: 100%;
        border: none;
        height: 24px;
        width: 24px;
        outline: none;
        cursor: pointer;
        margin-right: 4px;

        // override global css
        min-width: unset;
        background-image: none;
        padding: 0;

        .button-icon {
            margin-top: -3px;
        }
    }
    button:hover, button:active {
        background-color: $light-500;
    }

    button:disabled {
        background-color: $light-200;
        cursor: default;
    }
}
.infinity {
    font-size: 20px;
    color: $light-800;
    vertical-align: middle;
}
.stats-bar {
    width: calc(100% - 80px);
    display: inline-block;

    &.stats-bar-readonly {
        width: 100%;
        padding-right: 5px;
    }
}
</style>
